import { useCallback, useEffect, useRef, useState } from 'react';
import { BACKEND_URL } from '../../../shared/constants/api.constants';
import { apiGetIncomingInvitations } from '../../../shared/api/invitationFetch';
import {
  type BoardAssignmentAssignedNotificationPayload,
  type BoardAssignmentDeadlineNotificationPayload,
  type BoardAssignmentOverdueDigestNotificationPayload,
  type BoardCardCommentNotificationPayload,
  type CommentNotification,
  type CommentNotificationPayload,
  loadStoredCommentNotifications,
  saveStoredCommentNotifications
} from '../types/commentNotification';

type InvitationNotificationPayload = {
  type: 'invitation-notification';
  invitation: { id: string };
};

type InvitationRemovedPayload = {
  type: 'invitation-removed';
  invitationId: string;
};

const MAX_NOTIFICATIONS = 50;

interface UseUserCommentNotificationsOptions {
  userId: string | null | undefined;
  enabled?: boolean;
}

export const useUserCommentNotifications = ({
  userId,
  enabled = true
}: UseUserCommentNotificationsOptions) => {
  const [notifications, setNotifications] = useState<CommentNotification[]>([]);
  const [incomingInvitationCount, setIncomingInvitationCount] = useState(0);
  const knownInvitationIdsRef = useRef<Set<string>>(new Set());
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const isConnectingRef = useRef(false);

  useEffect(() => {
    if (!userId) {
      setNotifications([]);
      return;
    }
    setNotifications(loadStoredCommentNotifications(userId));
  }, [userId]);

  const persist = useCallback(
    (items: CommentNotification[]) => {
      if (!userId) return;
      saveStoredCommentNotifications(userId, items);
    },
    [userId]
  );

  const addNotification = useCallback(
    (payload: CommentNotificationPayload) => {
      if (!userId || payload.author.id === userId) return;

      setNotifications((prev) => {
        if (prev.some((item) => item.kind === 'file' && item.commentId === payload.commentId)) {
          return prev;
        }

        const next: CommentNotification[] = [
          {
            id: `${payload.commentId}-${Date.now()}`,
            kind: 'file' as const,
            commentId: payload.commentId,
            fileId: payload.fileId,
            fileName: payload.fileName,
            folderId: payload.folderId,
            folderName: payload.folderName,
            containingFolderId: payload.containingFolderId ?? payload.folderId,
            containingFolderName: payload.containingFolderName ?? payload.folderName,
            parentId: payload.parentId,
            isReply: payload.isReply,
            author: payload.author,
            textPreview: payload.textPreview,
            createdAt: payload.createdAt,
            receivedAt: new Date().toISOString()
          },
          ...prev
        ].slice(0, MAX_NOTIFICATIONS);

        persist(next);
        return next;
      });
    },
    [persist, userId]
  );

  const addBoardCardNotification = useCallback(
    (payload: BoardCardCommentNotificationPayload) => {
      if (!userId || payload.author.id === userId) return;

      setNotifications((prev) => {
        if (prev.some((item) => item.kind === 'board_card' && item.commentId === payload.commentId)) {
          return prev;
        }

        const next: CommentNotification[] = [
          {
            id: `${payload.commentId}-${Date.now()}`,
            kind: 'board_card' as const,
            commentId: payload.commentId,
            boardId: payload.boardId,
            rowId: payload.rowId,
            boardName: payload.boardName,
            cardTitle: payload.cardTitle,
            folderId: payload.rootFolderId,
            folderName: payload.folderName,
            parentId: payload.parentId,
            isReply: payload.isReply,
            author: payload.author,
            textPreview: payload.textPreview,
            createdAt: payload.createdAt,
            receivedAt: new Date().toISOString()
          },
          ...prev
        ].slice(0, MAX_NOTIFICATIONS);

        persist(next);
        return next;
      });
    },
    [persist, userId]
  );

  const addAssignmentDeadlineNotification = useCallback(
    (payload: BoardAssignmentDeadlineNotificationPayload) => {
      if (!userId) return;

      setNotifications((prev) => {
        const dedupeKey = `${payload.assignmentId}:${payload.tier}`;
        if (prev.some((item) => (
          item.kind === 'board_assignment_deadline'
          && `${item.assignmentId}:${item.tier}` === dedupeKey
        ))) {
          return prev;
        }

        const next: CommentNotification[] = [
          {
            id: `deadline-${payload.assignmentId}-${payload.tier}-${Date.now()}`,
            kind: 'board_assignment_deadline' as const,
            assignmentId: payload.assignmentId,
            boardId: payload.boardId,
            rowId: payload.rowId,
            boardName: payload.boardName,
            cardTitle: payload.cardTitle,
            folderId: payload.rootFolderId,
            folderName: payload.folderName,
            tier: payload.tier,
            deadlineAt: payload.deadlineAt,
            overdueDays: payload.overdueDays,
            author: {
              id: 'system',
              name: payload.boardName || 'Board',
              email: '',
            },
            createdAt: payload.timestamp || new Date().toISOString(),
            receivedAt: new Date().toISOString(),
          },
          ...prev,
        ].slice(0, MAX_NOTIFICATIONS);

        persist(next);
        return next;
      });
    },
    [persist, userId],
  );

  const addAssignmentDigestNotification = useCallback(
    (payload: BoardAssignmentOverdueDigestNotificationPayload) => {
      if (!userId) return;

      setNotifications((prev) => {
        if (prev.some((item) => (
          item.kind === 'board_assignment_digest'
          && item.assignmentId === payload.assignmentId
          && item.createdAt === payload.timestamp
        ))) {
          return prev;
        }

        const next: CommentNotification[] = [
          {
            id: `digest-${payload.assignmentId}-${Date.now()}`,
            kind: 'board_assignment_digest' as const,
            assignmentId: payload.assignmentId,
            boardId: payload.boardId,
            rowId: payload.rowId,
            boardName: payload.boardName,
            cardTitle: payload.cardTitle,
            folderId: payload.rootFolderId,
            folderName: payload.folderName,
            deadlineAt: payload.deadlineAt,
            overdueDays: payload.overdueDays,
            author: payload.assignee,
            createdAt: payload.timestamp || new Date().toISOString(),
            receivedAt: new Date().toISOString(),
          },
          ...prev,
        ].slice(0, MAX_NOTIFICATIONS);

        persist(next);
        return next;
      });
    },
    [persist, userId],
  );

  const addAssignmentAssignedNotification = useCallback(
    (payload: BoardAssignmentAssignedNotificationPayload) => {
      if (!userId) return;

      setNotifications((prev) => {
        if (prev.some((item) => (
          item.kind === 'board_assignment_assigned'
          && item.assignmentId === payload.assignmentId
        ))) {
          return prev;
        }

        const next: CommentNotification[] = [
          {
            id: `assigned-${payload.assignmentId}-${Date.now()}`,
            kind: 'board_assignment_assigned' as const,
            assignmentId: payload.assignmentId,
            boardId: payload.boardId,
            rowId: payload.rowId,
            boardName: payload.boardName,
            cardTitle: payload.cardTitle,
            folderId: payload.rootFolderId,
            folderName: payload.folderName,
            deadlineAt: payload.deadlineAt,
            targetColumnKey: payload.targetColumnKey,
            author: payload.assigner,
            createdAt: payload.timestamp || new Date().toISOString(),
            receivedAt: new Date().toISOString(),
          },
          ...prev,
        ].slice(0, MAX_NOTIFICATIONS);

        persist(next);
        return next;
      });
    },
    [persist, userId],
  );

  const dismissNotification = useCallback(
    (id: string) => {
      setNotifications((prev) => {
        const next = prev.filter((item) => item.id !== id);
        persist(next);
        return next;
      });
    },
    [persist]
  );

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
    persist([]);
  }, [persist]);

  const clearNotificationsForWorkspace = useCallback(
    (workspaceId: string) => {
      setNotifications((prev) => {
        const next = prev.filter((item) => item.folderId !== workspaceId);
        persist(next);
        return next;
      });
    },
    [persist]
  );

  const syncIncomingInvitationCount = useCallback(async () => {
    if (!userId) {
      knownInvitationIdsRef.current = new Set();
      setIncomingInvitationCount(0);
      return;
    }

    try {
      const invitations = await apiGetIncomingInvitations();
      knownInvitationIdsRef.current = new Set(invitations.map((item) => item.id));
      setIncomingInvitationCount(invitations.length);
    } catch (error) {
      console.error('Failed to sync incoming invitations:', error);
    }
  }, [userId]);

  const handleInvitationNotification = useCallback((payload: InvitationNotificationPayload) => {
    const invitationId = payload.invitation.id;
    if (knownInvitationIdsRef.current.has(invitationId)) return;

    knownInvitationIdsRef.current.add(invitationId);
    setIncomingInvitationCount((prev) => prev + 1);
  }, []);

  const handleInvitationRemoved = useCallback((payload: InvitationRemovedPayload) => {
    if (!knownInvitationIdsRef.current.has(payload.invitationId)) return;

    knownInvitationIdsRef.current.delete(payload.invitationId);
    setIncomingInvitationCount((prev) => Math.max(0, prev - 1));
  }, []);

  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    isConnectingRef.current = false;
  }, []);

  const connect = useCallback(() => {
    if (!userId || !enabled) return;
    if (isConnectingRef.current) return;
    isConnectingRef.current = true;

    disconnect();

    const eventSource = new EventSource(
      `${BACKEND_URL}/sse/user/notifications`,
      { withCredentials: true }
    );

    eventSourceRef.current = eventSource;

    eventSource.addEventListener('comment-notification', (e: MessageEvent) => {
      try {
        const data: CommentNotificationPayload = JSON.parse(e.data);
        addNotification(data);
      } catch (error) {
        console.error('Failed to parse comment-notification event:', error);
      }
    });

    eventSource.addEventListener('board-card-comment-notification', (e: MessageEvent) => {
      try {
        const data: BoardCardCommentNotificationPayload = JSON.parse(e.data);
        addBoardCardNotification(data);
      } catch (error) {
        console.error('Failed to parse board-card-comment-notification event:', error);
      }
    });

    eventSource.addEventListener('board-assignment-deadline-notification', (e: MessageEvent) => {
      try {
        const data: BoardAssignmentDeadlineNotificationPayload = JSON.parse(e.data);
        addAssignmentDeadlineNotification(data);
      } catch (error) {
        console.error('Failed to parse board-assignment-deadline-notification event:', error);
      }
    });

    eventSource.addEventListener('board-assignment-overdue-digest-notification', (e: MessageEvent) => {
      try {
        const data: BoardAssignmentOverdueDigestNotificationPayload = JSON.parse(e.data);
        addAssignmentDigestNotification(data);
      } catch (error) {
        console.error('Failed to parse board-assignment-overdue-digest-notification event:', error);
      }
    });

    eventSource.addEventListener('board-assignment-assigned-notification', (e: MessageEvent) => {
      try {
        const data: BoardAssignmentAssignedNotificationPayload = JSON.parse(e.data);
        addAssignmentAssignedNotification(data);
      } catch (error) {
        console.error('Failed to parse board-assignment-assigned-notification event:', error);
      }
    });

    eventSource.addEventListener('invitation-notification', (e: MessageEvent) => {
      const data: InvitationNotificationPayload = JSON.parse(e.data);
      handleInvitationNotification(data);
    });

    eventSource.addEventListener('invitation-removed', (e: MessageEvent) => {
      const data: InvitationRemovedPayload = JSON.parse(e.data);
      handleInvitationRemoved(data);
    });

    eventSource.onerror = () => {
      eventSource.close();
      eventSourceRef.current = null;
      isConnectingRef.current = false;

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      reconnectTimeoutRef.current = window.setTimeout(() => {
        if (enabled && userId) {
          connect();
        }
      }, 5000);
    };

    eventSource.onopen = () => {
      isConnectingRef.current = false;
    };
  }, [
    addAssignmentAssignedNotification,
    addAssignmentDeadlineNotification,
    addAssignmentDigestNotification,
    addBoardCardNotification,
    addNotification,
    disconnect,
    enabled,
    handleInvitationNotification,
    handleInvitationRemoved,
    userId
  ]);

  useEffect(() => {
    if (enabled && userId) {
      connect();
      syncIncomingInvitationCount();
    } else {
      disconnect();
      knownInvitationIdsRef.current = new Set();
      setIncomingInvitationCount(0);
    }

    return () => {
      disconnect();
    };
  }, [connect, disconnect, enabled, syncIncomingInvitationCount, userId]);

  return {
    notifications,
    notificationCount: notifications.length,
    incomingInvitationCount,
    syncIncomingInvitationCount,
    dismissNotification,
    clearAllNotifications,
    clearNotificationsForWorkspace
  };
};
